/**
 * Image utilities for handling image URLs and IDs
 * Provides functions to differentiate between direct image URLs and image IDs that need resolution
 * 
 * @example
 * ```typescript
 * import { isDirectImageUrl, isImageId } from '@/utils/imageUtils';
 * 
 * // Direct URLs (use as-is)
 * isDirectImageUrl('https://example.com/image.jpg'); // true
 * isDirectImageUrl('//cdn.example.com/image.jpg');   // true (protocol-relative)
 * isDirectImageUrl('/uploads/avatar.png');           // true
 * isDirectImageUrl('data:image/jpeg;base64,...');    // true
 * 
 * // Image IDs (need resolution via API)
 * isImageId('123e4567-e89b-12d3-a456-426614174000'); // true (UUID)
 * isImageId('image-id-123');                         // true (custom ID)
 * isImageId('simple-string');                        // true (any string)
 * 
 * // Usage in components
 * const needsResolution = isImageId(avatarUrl);
 * const finalUrl = needsResolution 
 *   ? useImageUrl(avatarUrl) 
 *   : isDirectImageUrl(avatarUrl) ? avatarUrl : undefined;
 * ```
 */

/**
 * Checks if a string is a direct image URL that doesn't need resolution
 * Returns true for: http/https URLs, protocol-relative URLs (//), relative paths starting with /, data URLs, blob URLs
 * Returns false for: UUIDs, empty strings, null/undefined
 * 
 * @param value - The string to check (null/undefined tolerated)
 * @returns true if the value is a direct image URL
 */
export const isDirectImageUrl = (value?: string | null): boolean => {
  if (!value?.trim()) {
    return false;
  }

  const trimmedValue = value.trim();
  
  return (
    /^https?:\/\//.test(trimmedValue) ||     // http/https URLs
    /^\/\//.test(trimmedValue) ||             // protocol-relative URLs
    /^\//.test(trimmedValue) ||               // relative paths starting with /
    /^data:/.test(trimmedValue) ||           // data URLs
    /^blob:/.test(trimmedValue)              // blob URLs
  );
};

/**
 * Checks if a string should be treated as an image ID that needs resolution
 * Returns true for any non-empty string that is not a direct image URL
 * Returns false for URLs, empty strings, null/undefined
 * 
 * Note: This includes both UUID-format strings and other string identifiers
 * The backend can handle various ID formats, not just UUIDs
 * 
 * @param value - The string to check (null/undefined tolerated)
 * @returns true if the value should be treated as an image ID
 */
export const isImageId = (value?: string | null): boolean => {
  if (!value?.trim()) {
    return false;
  }

  const trimmedValue = value.trim();
  
  // Don't treat URLs as image IDs
  if (isDirectImageUrl(trimmedValue)) {
    return false;
  }
  
  // Any other non-empty string should be treated as an image ID
  return true;
};